import React, { useEffect, useRef, useState } from "react";
import { FormTreeSelection } from "../../cpm/FormTree";
import { FormTable, SheetForm, TgkAmountObjectSource } from "../../interfaces/CoObject";
import { IChoiceGroupOption, IComboBoxOption } from "@fluentui/react";
import { CoObjectSourcePanelStateData } from "../co-table-panel/CoObjectSourceTablePanel";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { InputAreaSelectionEnum } from "../../../utils/constants/InputAreaSelectionEnum";

export interface TgkFormAmountSourceProps {
  isEdit?: boolean;
  objectSource: TgkAmountObjectSource;
  updateSource: (obj: TgkAmountObjectSource, tabValid, modified: boolean) => void;
  validateSource: (obj: TgkAmountObjectSource) => ObjectPanelTabs;
  selectDBAndFetchDefaultRoots: (option: IComboBoxOption) => void;
  isFileExcelExisting?: boolean;
  isComponentInstance?: boolean;
  isLocked?: boolean;
}

const useTgkFormAmountSourcePanel = (props: TgkFormAmountSourceProps) => {
  const {
    objectSource,
    updateSource,
    validateSource,
    selectDBAndFetchDefaultRoots,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
  } = props;
  const [selectedCellValue, setSelectedCellValue] = useState(objectSource.cell);
  const inputRef = useRef(null);
  const [currentCursorPosition, setCurrentCursorPosition] = useState(0);
  const [stateData, setStateData] = React.useState<CoObjectSourcePanelStateData>();
  const [sheetsAreaChartMap] = useState(new Map<string, string[]>());
  const [selectedChoice, setSelectedChoice] = useState("");
  const [hasSheetToBeSelected, setHasSheetToBeSelected] = useState<boolean>(false);
  const [isLoadingDataExcel, setIsLoadingDataExcel] = useState<boolean>(false);
  const choiceOptions: IChoiceGroupOption[] = [
    { key: InputAreaSelectionEnum.AREA_NAME.description, text: "Select area" },
    { key: InputAreaSelectionEnum.CELL.description, text: "Custom selection" },
  ];

  useEffect(() => {
    selectChoice();
    setStateData({
      areasItems: [],
      loading: true,
    });
  }, []);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.setSelectionRange(currentCursorPosition, currentCursorPosition);
    }
  }, [selectedCellValue]);

  const selectChoice = () => {
    objectSource.cell
      ? setSelectedChoice(InputAreaSelectionEnum.CELL.description)
      : setSelectedChoice(InputAreaSelectionEnum.AREA_NAME.description);
  };

  const onFormClick = (formSelection: FormTreeSelection) => {
    sheetsAreaChartMap.clear();
    const selectedForm = {
      code: formSelection.formCode,
      desc: formSelection.formDesc,
      oid: formSelection.key,
      oidPath: formSelection.oidPath,
    } as FormTable;

    const obj = {
      ...objectSource,
      form: selectedForm,
      sheet: null,
      area: null,
      cell: null,
      path: formSelection.oidPath,
    } as TgkAmountObjectSource;

    updateSource(obj, validateSource(obj), true);
  };

  const initAreas = (sheet: SheetForm) => {
    if (sheetsAreaChartMap) {
      sheetsAreaChartMap.forEach((value: string[], key: string) => {
        if (sheet && sheet.code == key) {
          const areaMap: IComboBoxOption[] = value?.map((area) => {
            return createIComboBoxFromAreas(area);
          });
          setStateData({
            ...stateData,
            areasItems: areaMap,
          });
        }
      });
    }
  };

  const createIComboBoxFromAreas = (areaValue: string): IComboBoxOption => {
    return {
      key: areaValue,
      text: areaValue,
    };
  };

  const selectSheetComboBox = (key?: string, text?: string) => {
    if (key === undefined) {
      const obj = {
        ...objectSource,
        sheet: null,
        area: null,
        cell: null,
      } as TgkAmountObjectSource;
      updateSource(obj, validateSource(obj), true);
      return;
    }
    const obj = {
      ...objectSource,
      sheet: { code: key, desc: text, uid: null },
      area: null,
      cell: null,
    } as TgkAmountObjectSource;

    updateSource(obj, validateSource(obj), true);
    initAreas({ code: key, desc: text } as SheetForm);
  };

  const onChangeChoice = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    setSelectedChoice(option.key);
    setSelectedCellValue(null);
    const obj = {
      ...objectSource,
      area: null,
      cell: null,
    } as TgkAmountObjectSource;
    updateSource(obj, validateSource(obj), true);
  };

  const selectAreaComboBox = (key?: string) => {
    if (key === undefined) {
      const obj = {
        ...objectSource,
        area: { name: null },
        cell: null,
      } as TgkAmountObjectSource;
      updateSource(obj, validateSource(obj), true);
      return;
    }
    const obj = {
      ...objectSource,
      area: { name: key },
      cell: null,
    } as TgkAmountObjectSource;
    updateSource(obj, validateSource(obj), true);
  };

  const onChangeCellValue = async (e) => {
    inputRef.current = e.currentTarget;
    setCurrentCursorPosition(e.currentTarget.selectionStart);

    const newValue = e.currentTarget.value.toUpperCase();
    setSelectedCellValue(newValue);
    if (areValidCells(newValue)) {
      setCellValue(newValue);
    } else {
      setCellValue(null);
    }
  };

  const areValidCells = (text: string) => {
    const regex = /^[A-Z]{1,4}[1-9]\d{0,3}$/;
    return regex.test(text);
  };

  const setCellValue = (cellName: string) => {
    const obj = {
      ...objectSource,
      area: null,
      cell: cellName,
    } as TgkAmountObjectSource;
    updateSource(obj, validateSource(obj), true);
  };

  return {
    objectSource,
    onFormClick,
    selectSheetComboBox,
    initAreas,
    sheetsAreaChartMap,
    selectedChoice,
    choiceOptions,
    onChangeChoice,
    selectAreaComboBox,
    onChangeCellValue,
    selectDBAndFetchDefaultRoots,
    isFileExcelExisting,
    isComponentInstance,
    isLocked,
    hasSheetToBeSelected,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    selectedCellValue,
  };
};

export default useTgkFormAmountSourcePanel;
